import React, { useState } from "react";
import { COLORS, FONTS } from "@/constants/theme";

/**
 * Card de presentacion de miembro del equipo con fallback visual.
 *
 * Muestra solo nombre, cargo y rol. La formacion academica se retiro a
 * peticion de Alejandro: no aporta y ata al equipo a una universidad.
 *
 * @param {{
 *   name: string,
 *   title: string,
 *   role: string,
 *   image: string,
 *   initials: string,
 *   mode?: "dark" | "light"
 * }} props
 * @returns {JSX.Element}
 */
const TeamCard = ({ name, title, role, image, initials, mode = "dark" }) => {
  const [imageError, setImageError] = useState(false);
  const showFallback = imageError || !image;
  const isLight = mode === "light";

  const cardBg = isLight ? COLORS.lightSurface : COLORS.bgPrimary;
  const cardBorder = isLight ? COLORS.lightBorder : COLORS.borderMedium;
  const titleColor = isLight ? COLORS.lightText : COLORS.textPrimary;
  const bodyColor = isLight ? COLORS.lightMuted : COLORS.textSecondary;

  return (
    <article
      className="team-card mx-auto w-full max-w-[300px] p-5"
      style={{
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: "24px",
        boxShadow: isLight ? "0 4px 24px rgba(0,104,255,0.07)" : COLORS.shadowCard,
        "--team-photo-hover": COLORS.digitalBlue,
      }}
    >
      {/* Foto circular centrada */}
      <div className="team-foto-marco mx-auto" style={{ display: "flex", justifyContent: "center", marginBottom: "1rem", width: "120px", height: "120px" }}>
        {showFallback ? (
          <div
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "2rem",
              fontFamily: FONTS.display,
              color: COLORS.textPrimary,
              background: COLORS.teamGradient,
              border: `3px solid ${cardBorder}`,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
        ) : (
          <img
            alt={name}
            className="team-photo object-cover"
            onError={() => setImageError(true)}
            src={image}
            loading="lazy"
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              border: `3px solid ${cardBorder}`,
              flexShrink: 0,
              // Las fotos ya vienen recortadas a cuadrado con el encuadre correcto.
              objectPosition: "center",
            }}
          />
        )}
      </div>

      <h3
        className="mt-1 text-xl"
        style={{
          color: titleColor,
          fontFamily: FONTS.display,
          textAlign: "center",
        }}
      >
        {name}
      </h3>

      <p
        className="mt-1 text-[0.9rem] uppercase"
        style={{
          color: COLORS.digitalBlue,
          fontFamily: FONTS.heading,
          letterSpacing: "0.08em",
          textAlign: "center",
        }}
      >
        {title}
      </p>

      <div style={{ display: "flex", justifyContent: "center", marginTop: "0.85rem" }}>
        <span
          className="inline-flex items-center rounded-full px-3 py-1 text-[0.82rem]"
          style={{
            color: COLORS.webOrange,
            backgroundColor: COLORS.badgeWarmBg,
            fontFamily: FONTS.body,
            border: `1px solid ${COLORS.borderWarm}`,
          }}
        >
          {role}
        </span>
      </div>
    </article>
  );
};

export default TeamCard;
